// NOTES

// overview of possible comparisons
// data point should be sorted before being processed

// x1 < y1
// x1 === y1
// x1 > y1 -- if sorted should never happen

// x1 < y2 -- expected
// x1 === y2 -- if sorted should never happen
// x1 > y2 -- if sorted should never happen

// x2 < y1 - no overlap
// x2 === y1 - no overlap
// x2 > y1

// x2 < y2
// x2 === y2 -- if sorted x and y are the same
// x2 > y2

import { get } from "lodash";
import moment from "moment-timezone";

// creates hour obj array from passed data point array
function buildHourRange(key, data) {
  const from = moment.utc(data.from);
  const to = moment.utc(data.to);

  let hours = [];

  // account for dates
  const numHours = to.diff(from, "hours");
  let currHour = from.clone();

  for (let i = 0; i < numHours; i++) {
    const id = `h-${currHour.month()}-${currHour.date()}-${currHour.hour()}`;

    const timeObj = {
      id: id,
      hour: currHour.hour(),
      from: currHour.format(),
      value: data.value,
      dimension: data.dimension
    };

    hours.push(timeObj);

    currHour.add(1, "hour");
  }

  return hours;
}

// creates day obj from passed hour obj array
function buildDay(id, data) {
  const day = moment.utc(data[0].from);

  const result = {
    id: id,
    label: day.format("Do MMM"),
    days: data
  };

  return result;
}

// creates data point with padded value of -1
function padData(from, to) {
  return {
    value: -1,
    dimension: "s",
    from: from.format(),
    to: to.format()
  };
}

// pads out missing data points.
function parseDataIntegrity(data) {
  const result = data.slice();

  let numPads = 0;

  for (let i = 0; i < data.length; i++) {
    if (data[i + 1]) {
      const to = moment.utc(data[i].to);
      const from = moment.utc(data[i + 1].from);

      if (to.unix() !== from.unix()) {
        const pad = padData(to, from);
        result.splice(i + 1 + numPads, 0, pad);
        numPads += 1;
      }
    }
  }

  return result;
}

// assumes that all datapoints have already been rounded to the nearest hour.
// pad data ends to round out full days.
function padDataEnds(data) {
  const result = data.slice();
  const first = moment.utc(data[0].from);
  const last = moment.utc(data[data.length - 1].to);

  // pad start of data if required
  if (first.hour() > 0) {
    const pad = first.clone().hour(0);
    result.unshift(padData(pad, first));
  }

  // if the last element doesn't end at midnight
  // pad end of data if required
  if (last.hour() > 0) {
    // can't use last.endOf("day") as it returns 23:59 while API data points use 00:00 of the next day for midnight
    const end = last.clone().add(1, "day").startOf("day");

    result.push(padData(last, end));
  }

  return result;
}

// make sure all data points start and end on the hour
// ignores overlaps
function roundData(data) {
  const result = data.slice();

  for (let i = 0; i < data.length; i++) {
    const dataPoint = data[i];
    const from = moment.utc(dataPoint.from);
    const to = moment.utc(dataPoint.to);

    const roundedFrom = from.clone().startOf("hour").format();

    let roundedTo = to.format();
    if (to.minute() > 0 || to.second() > 0 || to.millisecond() > 0) {
      roundedTo = to.clone().startOf("hour").add(1, "hour").format();
    }

    result[i] = {
      ...dataPoint,
      from: roundedFrom,
      to: roundedTo
    };
  }

  return result;
}

function sortByFromTo(x, y) {
  const x1 = moment.utc(x.from).unix();
  const x2 = moment.utc(x.to).unix();
  const y1 = moment.utc(y.from).unix();
  const y2 = moment.utc(y.to).unix();

  // initially order by from
  let comparison = 0;
  if (x1 > y1) {
    comparison = 1;
  } else if (x1 < y1) {
    comparison = -1;
  }
  // secondly order by to
  if (x2 > y2 && comparison === 0) {
    comparison = -1;
  } else if (x2 < y2 && comparison === 0) {
    comparison = 1;
  }
  return comparison;
}

// pads out missing data points.
// data MUST be sorted on from value before doing this
function removeOverlaps(origData) {
  let data = origData.slice();

  let i = 0;

  do {
    const x = data[i];
    const y = data[i + 1];

    if (!y) break;

    const x1 = moment.utc(x.from).unix();
    const x2 = moment.utc(x.to).unix();
    const y1 = moment.utc(y.from).unix();
    const y2 = moment.utc(y.to).unix();

    // there's no overlap so move on
    if (!(x1 < y2 && y1 < x2)) {
      i++;
      continue;
    }

    // assumptions
    // - data points are pre sorted ∴ x1 < y1 === false
    // - data points always overlaps ∴ x2 <= y1 === false (these cases have been skipped above)

    // check whether x consumes y
    if (x1 <= y1 && x2 >= y2) {
      if (x.value >= y.value) {
        // x consumes y
        data.splice(i + 1, 1);
        continue; // don't increment as we need to check the shifted data point
      } else {
        // x splits y
        // { x1:x1, x2:y1 }
        // { y1:y1, y2:y2 }
        // { z1:y2, z2:x2 }
        data.splice(i, 2, { ...x, to: y.from }, { ...y }, { ...x, from: y.to });
      }
    }

    // mange how x intrudes into y
    if (y1 < x2 < y2) {
      if (x.value === y.value) {
        // y1 = x2
        data[i] = {
          ...x,
          to: y.to
        };
        data.splice(i + 1, 1);
      } else if (x.value > y.value) {
        data[i + 1] = {
          ...y,
          from: x.to
        };
      } else {
        // x2 = y1
        if (x1 !== y1) {
          data[i] = {
            ...x,
            to: y.from
          };
        } else {
          data.splice(i, 1);
        }
      }
    }

    i++;
  } while (i < data.length - 1);

  return data;
}

export function sanitiseData(data) {
  // first ensure data is consistant and complete
  // implemented these processes separately to avoid brain fry - could be combined
  const roundedData = roundData(data);
  const sortedData = roundedData.slice().sort(sortByFromTo);
  const endPaddedData = padDataEnds(sortedData);
  const unOverlappedData = removeOverlaps(endPaddedData);
  const parsedData = parseDataIntegrity(unOverlappedData);

  return parsedData;
}

export function buildMeasurementData(data) {
  const parsedData = sanitiseData(data);

  // create hour block array for entire data range
  let hours = [];
  for (let i = 0; i < parsedData.length; i++) {
    hours = hours.concat(buildHourRange(i, parsedData[i]));
  }

  // check for DST changes
  // what about none 1 hour changes?
  for (let i = 0; i < hours.length; i++) {
    const prevPos = i - 1;
    const prevObj = get(hours, `[${prevPos}]`, null);
    const currObj = get(hours, `[${i}]`, null);

    if (prevObj && currObj) {
      if (prevObj.id === currObj.id) {
        // console.log("!!! DUPLICATE !!!");
        hours[prevPos] = {
          ...prevObj,
          sub: {
            ...currObj
          }
        };

        hours.splice(i, 1);
        i--;
      } else if (currObj.hour - prevObj.hour > 1) {
        // console.log("!!! MISSING TIME !!!");
        const { id, hour, from, dimension } = prevObj;
        const newId = `${id.substring(0, id.lastIndexOf("-") + 1)}${hour + 1}`;

        const newObj = {
          id: newId,
          hour: hour + 1,
          from,
          value: -1,
          dimension,
          gap: true
        };

        hours.splice(i, 0, newObj);
      }
    }
  }

  // group hour blocks into 24 hour day arrays
  let days = [];
  for (let j = 0; j < hours.length; j += 24) {
    days.push(buildDay(j, hours.slice(j, j + 24)));
  }

  return days;
}
